import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/LanguageContext";
import { Reveal } from "@/components/Reveal";
import {
  Sparkles,
  Smile,
  AlignCenter,
  Anchor,
  Sun,
  Crown,
  Eye,
  Droplets,
  ArrowUpRight,
} from "lucide-react";

const ICONS = [Sparkles, Smile, AlignCenter, Anchor, Sun, Crown, Eye, Droplets];
const SLUGS = [
  "smile-design",
  "veneers",
  "orthodontics",
  "implants",
  "whitening",
  "rehab",
  "invisalign",
  "cleaning",
];

export const Services = () => {
  const { t } = useLang();

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-white py-24 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
          <Reveal className="lg:col-span-7">
            <p className="overline mb-6">— {t.services.overline}</p>
            <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1A1A1A]">
              {t.services.title}{" "}
              <span className="italic text-[#D4AF37]">
                {t.services.title_accent}
              </span>
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.1}>
            <div className="gold-line mb-6" />
            <p className="text-[#666666] text-base md:text-lg leading-relaxed">
              Cada servicio en Multident está diseñado con un estándar
              clínico-estético al nivel de las mejores clínicas del mundo.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E5E5] border border-[#E5E5E5] rounded-3xl overflow-hidden">
          {t.services.list.map((s, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <motion.article
                key={i}
                data-testid={`service-card-${SLUGS[i]}`}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{
                  duration: 0.8,
                  delay: (i % 4) * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
                whileHover={{ y: -4 }}
                className="group relative bg-[#F9F8F6] p-8 md:p-10 cursor-pointer hover:bg-white transition-colors duration-500"
              >
                <div className="flex items-start justify-between mb-12">
                  <motion.div
                    whileHover={{ rotate: 12, scale: 1.08 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="w-12 h-12 rounded-full border border-[#1A1A1A]/15 flex items-center justify-center group-hover:border-[#D4AF37] group-hover:bg-white transition-all"
                  >
                    <Icon
                      size={20}
                      strokeWidth={1.4}
                      className="text-[#1A1A1A] group-hover:text-[#D4AF37] transition-colors"
                    />
                  </motion.div>
                  <span className="text-[10px] tracking-[0.25em] text-[#666666]">
                    0{i + 1}
                  </span>
                </div>

                <h3 className="font-display text-xl md:text-2xl text-[#1A1A1A] tracking-tight mb-3">
                  {s.t}
                </h3>
                <p className="text-sm text-[#666666] leading-relaxed mb-8">
                  {s.d}
                </p>
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#1A1A1A] group-hover:text-[#D4AF37] transition-colors">
                  {t.services.learn}
                  <motion.span
                    animate={{}}
                    className="inline-block"
                  >
                    <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </motion.span>
                </span>

                <span className="absolute left-0 right-0 bottom-0 h-px bg-[#D4AF37] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
